import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { useDateFormat } from '../contexts/DateFormatContext';
import { useOddsFormat } from '../contexts/OddsFormatContext';
import DateInput from '../components/DateInput';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Trash2, Edit, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BetHistory = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { formatDate } = useDateFormat();
  const { convertOdds } = useOddsFormat();
  const [bets, setBets] = useState([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    league: '',
    tipster: '',
    strategy: '',
    result: '',
    bet_type: '',
    start_date: '',
    end_date: ''
  });
  const [selectedBet, setSelectedBet] = useState(null);
  const [updateData, setUpdateData] = useState({ result: '', profit_loss: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedBets, setExpandedBets] = useState({}); // Track expanded accumulators
  const [selectionUpdateDialog, setSelectionUpdateDialog] = useState({ open: false, bet: null, selection: null });

  useEffect(() => {
    fetchBets();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [bets, filters]);

  const fetchBets = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/bets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBets(response.data);
    } catch (error) {
      console.error('Error fetching bets:', error);
      toast.error('Failed to load bets');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bets];

    if (filters.league) {
      filtered = filtered.filter(bet => bet.league.toLowerCase().includes(filters.league.toLowerCase()));
    }
    if (filters.tipster) {
      filtered = filtered.filter(bet => bet.tipster?.toLowerCase().includes(filters.tipster.toLowerCase()));
    }
    if (filters.strategy) {
      filtered = filtered.filter(bet => bet.strategy?.toLowerCase().includes(filters.strategy.toLowerCase()));
    }
    if (filters.result) {
      filtered = filtered.filter(bet => bet.result === filters.result);
    }
    if (filters.bet_type) {
      filtered = filtered.filter(bet => (bet.bet_type || 'single') === filters.bet_type);
    }
    if (filters.start_date) {
      filtered = filtered.filter(bet => new Date(bet.bet_date) >= new Date(filters.start_date));
    }
    if (filters.end_date) {
      filtered = filtered.filter(bet => new Date(bet.bet_date) <= new Date(filters.end_date));
    }

    // Sort by date - newest first (descending order)
    filtered.sort((a, b) => new Date(b.bet_date) - new Date(a.bet_date));

    setFilteredBets(filtered);
  };

  const handleUpdateBet = async (betId) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${API}/bets/${betId}`, {
        result: updateData.result,
        profit_loss: parseFloat(updateData.profit_loss)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Bet updated successfully!');
      setIsDialogOpen(false); // Close the dialog
      setSelectedBet(null);
      fetchBets();
    } catch (error) {
      console.error('Error updating bet:', error);
      toast.error('Failed to update bet');
    }
  };

  const handleDeleteBet = async (betId) => {
    if (!window.confirm('Are you sure you want to delete this bet?')) return;

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/bets/${betId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Bet deleted successfully!');
      fetchBets();
    } catch (error) {
      console.error('Error deleting bet:', error);
      toast.error('Failed to delete bet');
    }
  };

  const toggleExpanded = (betId) => {
    setExpandedBets(prev => ({ ...prev, [betId]: !prev[betId] }));
  };

  const handleUpdateSelection = async (betId, selectionId, result) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${API}/bets/${betId}/selection/${selectionId}`, {
        selection_id: selectionId,
        result: result
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Selection updated!');
      setSelectionUpdateDialog({ open: false, bet: null, selection: null });
      fetchBets();
    } catch (error) {
      console.error('Error updating selection:', error);
      toast.error('Failed to update selection');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="bet-history-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8">Bet History</h1>

          {/* Filters */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="league-filter" className="text-white">Filter by League</Label>
                  <Input
                    data-testid="filter-league"
                    id="league-filter"
                    value={filters.league}
                    onChange={(e) => setFilters({ ...filters, league: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="Enter league name"
                  />
                </div>
                <div>
                  <Label htmlFor="tipster-filter" className="text-white">Filter by Tipster</Label>
                  <Input
                    data-testid="filter-tipster"
                    id="tipster-filter"
                    value={filters.tipster}
                    onChange={(e) => setFilters({ ...filters, tipster: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="Enter tipster name"
                  />
                </div>
                <div>
                  <Label htmlFor="strategy-filter" className="text-white">Filter by Strategy</Label>
                  <Input
                    data-testid="filter-strategy"
                    id="strategy-filter"
                    value={filters.strategy}
                    onChange={(e) => setFilters({ ...filters, strategy: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="Enter strategy name"
                  />
                </div>
                <div>
                  <Label htmlFor="result-filter" className="text-white">Filter by Result</Label>
                  <Select data-testid="filter-result" value={filters.result} onValueChange={(value) => setFilters({ ...filters, result: value === 'all' ? '' : value })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                      <SelectValue placeholder="All Results" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Results</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="won">Won</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                      <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bet-type-filter" className="text-white">Filter by Bet Type</Label>
                  <Select value={filters.bet_type} onValueChange={(value) => setFilters({ ...filters, bet_type: value === 'all' ? '' : value })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="accumulator">Accumulator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <DateInput
                    label="Start Date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <DateInput
                    label="End Date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    className="mt-2"
                  />
                </div>
              </div>
              <Button
                data-testid="clear-filters-button"
                onClick={() => setFilters({ league: '', tipster: '', strategy: '', result: '', bet_type: '', start_date: '', end_date: '' })}
                variant="outline"
                className="mt-4 border-white/20 text-white hover:bg-white/10"
              >
                Clear Filters
              </Button>
            </CardContent>
          </Card>

          {/* Bets List */}
          {filteredBets.length === 0 ? (
            <Card className="glass-dark border-white/10">
              <CardContent className="py-12 text-center">
                <p data-testid="no-bets-message" className="text-gray-400 text-lg">No bets found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredBets.map((bet, index) => (
                <Card key={bet.id} data-testid={`bet-card-${index}`} className="glass-dark border-white/10 card-hover">
                  <CardContent className="pt-6">
                    {/* Accumulator Badge */}
                    {bet.bet_type === 'accumulator' && (
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          <Layers className="w-3 h-3" />
                          Accumulator ({bet.num_selections || bet.selections?.length || 0} selections)
                        </span>
                      </div>
                    )}
                    
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">
                          {bet.bet_type === 'accumulator' 
                            ? `${bet.num_selections || bet.selections?.length || 0}-Fold Accumulator`
                            : `${bet.home_team} vs ${bet.away_team}`
                          }
                        </h3>
                        <div className="space-y-1 text-sm text-gray-400">
                          {bet.bet_type !== 'accumulator' && (
                            <>
                              <p>League: {bet.league}</p>
                              <p>Market: {bet.market}</p>
                            </>
                          )}
                          {bet.tipster && <p>Tipster: {bet.tipster}</p>}
                          {bet.strategy && <p>Strategy: {bet.strategy}</p>}
                          <p>Total Odds: {convertOdds(bet.odds)} | Stake: <Price amount={bet.stake} /></p>
                          <p>Potential Return: <Price amount={bet.potential_return} /></p>
                          <p>Date: {formatDate(bet.bet_date)}</p>
                        </div>
                      </div>
                      <div className="flex flex-col justify-between items-end gap-2">
                        <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                          bet.result === 'won' ? 'bg-green-500/20 text-green-400' :
                          bet.result === 'lost' ? 'bg-red-500/20 text-red-400' :
                          bet.result === 'void' ? 'bg-gray-500/20 text-gray-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {bet.result || 'pending'}
                        </span>
                        {bet.profit_loss !== null && (
                          <p className={`text-lg font-bold ${
                            bet.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {bet.profit_loss >= 0 ? '+' : ''}<Price amount={bet.profit_loss} />
                          </p>
                        )}
                        <div className="flex gap-2">
                          {/* Expand button for accumulators */}
                          {bet.bet_type === 'accumulator' && bet.selections && bet.selections.length > 0 && (
                            <Button
                              onClick={() => toggleExpanded(bet.id)}
                              size="sm"
                              variant="outline"
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                            >
                              {expandedBets[bet.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          )}
                          
                          {/* Edit button for accumulators - expands to show selection editors */}
                          {bet.bet_type === 'accumulator' && bet.selections && bet.selections.length > 0 && (
                            <Button
                              data-testid={`edit-bet-${index}`}
                              onClick={() => {
                                // Expand the accumulator to show selection editors
                                setExpandedBets(prev => ({ ...prev, [bet.id]: true }));
                              }}
                              size="sm"
                              variant="outline"
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                              title="Edit selections"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Edit button - only for single bets */}
                          {bet.bet_type !== 'accumulator' && (
                            <Dialog open={isDialogOpen && selectedBet?.id === bet.id} onOpenChange={(open) => {
                              setIsDialogOpen(open);
                              if (!open) setSelectedBet(null);
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  data-testid={`edit-bet-${index}`}
                                  onClick={() => {
                                    setSelectedBet(bet);
                                    setUpdateData({ result: bet.result || '', profit_loss: bet.profit_loss || '' });
                                    setIsDialogOpen(true);
                                  }}
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="bg-slate-900 border-white/10">
                                <DialogHeader>
                                  <DialogTitle className="text-white">Update Bet Result</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                  <div>
                                    <Label htmlFor="result" className="text-white">Result</Label>
                                    <Select 
                                      value={updateData.result} 
                                      onValueChange={(value) => {
                                        // Auto-calculate profit/loss based on result
                                        let calculatedProfitLoss = updateData.profit_loss;
                                        if (selectedBet) {
                                          const stake = parseFloat(selectedBet.stake) || 0;
                                          const odds = parseFloat(selectedBet.odds) || 1;
                                          
                                          if (value === 'won') {
                                            // Profit = stake * (odds - 1) for decimal odds
                                            calculatedProfitLoss = (stake * (odds - 1)).toFixed(2);
                                          } else if (value === 'void') {
                                            calculatedProfitLoss = '0.00';
                                          } else if (value === 'lost') {
                                            calculatedProfitLoss = (-stake).toFixed(2);
                                          }
                                        }
                                        setUpdateData({ result: value, profit_loss: calculatedProfitLoss });
                                      }}
                                    >
                                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                                        <SelectValue placeholder="Select result" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="won">Won</SelectItem>
                                        <SelectItem value="lost">Lost</SelectItem>
                                        <SelectItem value="void">Void</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label htmlFor="profit_loss" className="text-white">
                                      Profit/Loss ({selectedBet?.stake ? `Stake: ${selectedBet.stake}` : '$'})
                                    </Label>
                                    <Input
                                      id="profit_loss"
                                      type="number"
                                      step="0.01"
                                      value={updateData.profit_loss}
                                      onChange={(e) => setUpdateData({ ...updateData, profit_loss: e.target.value })}
                                      className="bg-white/5 border-white/10 text-white mt-2"
                                      placeholder="Auto-calculated, but editable"
                                    />
                                  </div>
                                  <Button
                                    onClick={() => handleUpdateBet(bet.id)}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    Update
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                          
                          <Button
                            data-testid={`delete-bet-${index}`}
                            onClick={() => handleDeleteBet(bet.id)}
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Selections for Accumulators */}
                    {bet.bet_type === 'accumulator' && expandedBets[bet.id] && bet.selections && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <h4 className="text-white font-semibold mb-3">Selections</h4>
                        <div className="space-y-2">
                          {bet.selections.map((sel, selIndex) => (
                            <div 
                              key={sel.id || selIndex} 
                              className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                            >
                              <div className="flex-1">
                                <div className="text-white font-medium">
                                  {selIndex + 1}. {sel.home_team} vs {sel.away_team}
                                </div>
                                <div className="text-gray-400 text-sm">
                                  {sel.league} • {sel.market} • Odds: {convertOdds(sel.odds)}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  sel.result === 'won' ? 'bg-green-500/20 text-green-400' :
                                  sel.result === 'lost' ? 'bg-red-500/20 text-red-400' :
                                  sel.result === 'void' ? 'bg-gray-500/20 text-gray-400' :
                                  'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {sel.result || 'pending'}
                                </span>
                                {/* Allow updating selection result - always show edit option */}
                                <Select
                                  value={sel.result || 'pending'}
                                  onValueChange={(value) => handleUpdateSelection(bet.id, sel.id, value)}
                                >
                                  <SelectTrigger className="w-24 h-8 bg-white/5 border-white/10 text-white text-xs">
                                    <SelectValue placeholder="Update" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="won">Won</SelectItem>
                                    <SelectItem value="lost">Lost</SelectItem>
                                    <SelectItem value="void">Void</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BetHistory;
